/**
 * @module StammdatenPage
 * @description Öffentliche Seite für Bewerber-Stammdaten-Selbsterfassung.
 *   Kein Login nötig, Token-basierter Zugang.
 * @route /stammdaten/:token
 * @since v2.231.0
 * @updated v2.846.0 — #1779: WeShip-Logo, durchgängige Du-Ansprache, i18n (de/en).
 */
import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';

const API = import.meta.env.VITE_API_URL || '';

export default function StammdatenPage() {
  const { token } = useParams();
  const { t, language, setLanguage } = useLanguage();
  useEffect(() => { if (language !== 'de') setLanguage('de'); }, []); // DE als Standard (wie /karriere)

  const [loading, setLoading] = useState(true);
  const [bewerber, setBewerber] = useState(null);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);

  const [form, setForm] = useState({
    svnr: '', geburtsdatum: '', strasse: '', plz: '', ort: '', land: 'AT',
    iban: '', notfallkontakt_name: '', notfallkontakt_telefon: '',
  });
  const [foto, setFoto] = useState(null);
  const [fotoPreview, setFotoPreview] = useState(null);

  useEffect(() => {
    if (!token) return;
    fetch(`${API}/api/public/recruiting/stammdaten/${token}`)
      .then(r => {
        if (!r.ok) throw new Error('not_found');
        return r.json();
      })
      .then(data => {
        setBewerber(data);
        if (data.bereits_ausgefuellt) setSubmitted(true);
        // Pre-fill existing data
        setForm(prev => ({
          ...prev,
          svnr: data.svnr || '',
          geburtsdatum: data.geburtsdatum ? data.geburtsdatum.substring(0, 10) : '',
          strasse: data.strasse || '',
          plz: data.plz || '',
          ort: data.ort || '',
          land: data.land || 'AT',
          iban: data.iban || '',
          notfallkontakt_name: data.notfallkontakt_name || '',
          notfallkontakt_telefon: data.notfallkontakt_telefon || '',
        }));
      })
      .catch(() => setError(t('stammdaten.fehler.link', 'Dieser Link ist ungültig oder abgelaufen.')))
      .finally(() => setLoading(false));
  }, [token]);

  const handleChange = (e) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleFoto = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setFoto(file);
    const reader = new FileReader();
    reader.onloadend = () => setFotoPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.svnr.trim() || !form.geburtsdatum || !form.strasse.trim() || !form.plz.trim() || !form.ort.trim()) {
      setFormError(t('stammdaten.fehler.pflichtfelder', 'Bitte fülle alle Pflichtfelder aus.'));
      return;
    }
    setSaving(true);
    setFormError(null);

    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => { if (v) fd.append(k, v); });
      if (foto) fd.append('foto', foto);

      const res = await fetch(`${API}/api/public/recruiting/stammdaten/${token}`, {
        method: 'POST',
        body: fd,
      });

      if (res.ok) {
        setSubmitted(true);
      } else {
        const data = await res.json().catch(() => ({}));
        setFormError(data.error || t('stammdaten.fehler.speichern', 'Fehler beim Speichern.'));
      }
    } catch {
      setFormError(t('stammdaten.fehler.verbindung', 'Verbindungsfehler. Bitte versuche es erneut.'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#F4F5FA', fontFamily: 'DM Sans, sans-serif' }}>
        <div className="animate-spin w-8 h-8 border-3 border-t-transparent rounded-full" style={{ borderColor: '#338edc', borderTopColor: 'transparent' }} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#F4F5FA', fontFamily: 'DM Sans, sans-serif' }}>
        <div className="bg-white rounded-2xl border p-10 max-w-md w-full mx-4 text-center" style={{ borderColor: '#E5E7EB' }}>
          <div className="text-4xl mb-4">{'\u{1F6AB}'}</div>
          <h1 className="text-xl font-bold mb-2" style={{ color: '#1A1A2E' }}>{t('stammdaten.linkUngueltig', 'Link ungültig')}</h1>
          <p style={{ color: '#6B7280' }}>{error}</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#F4F5FA', fontFamily: 'DM Sans, sans-serif' }}>
        <div className="bg-white rounded-2xl border p-10 max-w-md w-full mx-4 text-center" style={{ borderColor: '#E5E7EB' }}>
          <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center text-3xl" style={{ background: '#ECFDF5' }}>
            {'\u2705'}
          </div>
          <h2 className="text-xl font-bold mb-2" style={{ color: '#1A1A2E' }}>{t('stammdaten.danke.titel', 'Stammdaten übermittelt!')}</h2>
          <p style={{ color: '#4B5563' }}>
            {t('stammdaten.danke.text', 'Vielen Dank, {{vorname}}. Deine Daten wurden erfolgreich gespeichert. Wir melden uns bei dir wegen der nächsten Schritte.', { vorname: bewerber?.vorname || '' })}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: '#F4F5FA', fontFamily: 'DM Sans, sans-serif' }}>
      {/* Header */}
      <div className="bg-white border-b" style={{ borderColor: '#E5E7EB' }}>
        <div className="max-w-2xl mx-auto px-6 py-4 flex items-center gap-3">
          <img src="/weship-logo.png" alt="WeShip Fulfillment" className="h-9 w-auto" />
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-6 py-10">
        {/* Info */}
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold mb-2" style={{ color: '#1A1A2E' }}>
            {t('stammdaten.titel', 'Stammdaten erfassen')}
          </h1>
          <p style={{ color: '#4B5563' }}>
            {t('stammdaten.intro', 'Hallo {{vorname}}, bitte fülle die folgenden Daten aus, damit wir deinen Arbeitsvertrag vorbereiten können.', { vorname: bewerber?.vorname || '' })}
          </p>
        </div>

        <div className="bg-white rounded-2xl border p-6" style={{ borderColor: '#E5E7EB' }}>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Foto */}
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: '#4B5563' }}>{t('stammdaten.feld.foto', 'Foto (für Mitarbeiterausweis)')}</label>
              <div className="flex items-center gap-4">
                {fotoPreview ? (
                  <img src={fotoPreview} alt="Preview" className="w-20 h-20 rounded-xl object-cover border" style={{ borderColor: '#E5E7EB' }} />
                ) : (
                  <div className="w-20 h-20 rounded-xl border-2 border-dashed flex items-center justify-center text-2xl" style={{ borderColor: '#D1D5DB', color: '#9CA3AF' }}>
                    {'\u{1F4F7}'}
                  </div>
                )}
                <input type="file" accept="image/*" onChange={handleFoto}
                  className="text-sm file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border file:text-sm file:font-medium"
                  style={{ color: '#4B5563' }} />
              </div>
            </div>

            {/* SV-Nummer + Geburtsdatum */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: '#4B5563' }}>{t('stammdaten.feld.svnr', 'Sozialversicherungsnummer')} *</label>
                <input name="svnr" value={form.svnr} onChange={handleChange} required
                  className="w-full px-3 py-2.5 rounded-lg border text-sm" style={{ borderColor: '#E5E7EB', color: '#1A1A2E' }}
                  placeholder="1234 010190" maxLength={15} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: '#4B5563' }}>{t('stammdaten.feld.geburtsdatum', 'Geburtsdatum')} *</label>
                <input name="geburtsdatum" type="date" value={form.geburtsdatum} onChange={handleChange} required
                  className="w-full px-3 py-2.5 rounded-lg border text-sm" style={{ borderColor: '#E5E7EB', color: '#1A1A2E' }} />
              </div>
            </div>

            {/* Adresse */}
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: '#4B5563' }}>{t('stammdaten.feld.strasse', 'Straße + Hausnummer')} *</label>
              <input name="strasse" value={form.strasse} onChange={handleChange} required
                className="w-full px-3 py-2.5 rounded-lg border text-sm" style={{ borderColor: '#E5E7EB', color: '#1A1A2E' }}
                placeholder="Musterstraße 12" />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: '#4B5563' }}>{t('stammdaten.feld.plz', 'PLZ')} *</label>
                <input name="plz" value={form.plz} onChange={handleChange} required
                  className="w-full px-3 py-2.5 rounded-lg border text-sm" style={{ borderColor: '#E5E7EB', color: '#1A1A2E' }}
                  placeholder="8141" maxLength={10} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: '#4B5563' }}>{t('stammdaten.feld.ort', 'Ort')} *</label>
                <input name="ort" value={form.ort} onChange={handleChange} required
                  className="w-full px-3 py-2.5 rounded-lg border text-sm" style={{ borderColor: '#E5E7EB', color: '#1A1A2E' }}
                  placeholder="Premstätten" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: '#4B5563' }}>{t('stammdaten.feld.land', 'Land')}</label>
                <select name="land" value={form.land} onChange={handleChange}
                  className="w-full px-3 py-2.5 rounded-lg border text-sm" style={{ borderColor: '#E5E7EB', color: '#1A1A2E' }}>
                  <option value="AT">{t('stammdaten.land.at', 'Österreich')}</option>
                  <option value="DE">{t('stammdaten.land.de', 'Deutschland')}</option>
                  <option value="HU">{t('stammdaten.land.hu', 'Ungarn')}</option>
                  <option value="SI">{t('stammdaten.land.si', 'Slowenien')}</option>
                  <option value="HR">{t('stammdaten.land.hr', 'Kroatien')}</option>
                </select>
              </div>
            </div>

            {/* IBAN */}
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: '#4B5563' }}>{t('stammdaten.feld.iban', 'IBAN')}</label>
              <input name="iban" value={form.iban} onChange={handleChange}
                className="w-full px-3 py-2.5 rounded-lg border text-sm font-mono" style={{ borderColor: '#E5E7EB', color: '#1A1A2E' }}
                placeholder="AT12 3456 7890 1234 5678" maxLength={34} />
            </div>

            {/* Notfallkontakt */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: '#4B5563' }}>{t('stammdaten.feld.notfallName', 'Notfallkontakt Name')}</label>
                <input name="notfallkontakt_name" value={form.notfallkontakt_name} onChange={handleChange}
                  className="w-full px-3 py-2.5 rounded-lg border text-sm" style={{ borderColor: '#E5E7EB', color: '#1A1A2E' }}
                  placeholder={t('stammdaten.feld.notfallNamePlatzhalter', 'Name')} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: '#4B5563' }}>{t('stammdaten.feld.notfallTelefon', 'Notfallkontakt Telefon')}</label>
                <input name="notfallkontakt_telefon" value={form.notfallkontakt_telefon} onChange={handleChange}
                  className="w-full px-3 py-2.5 rounded-lg border text-sm" style={{ borderColor: '#E5E7EB', color: '#1A1A2E' }}
                  placeholder="+43 ..." />
              </div>
            </div>

            {formError && (
              <div className="rounded-lg p-3 text-sm font-medium" style={{ background: '#FEF2F2', color: '#991B1B' }}>
                {formError}
              </div>
            )}

            <button type="submit" disabled={saving}
              className="w-full py-3 rounded-xl text-white font-semibold text-base transition-colors disabled:opacity-60"
              style={{ background: '#338edc' }}>
              {saving ? t('stammdaten.speichert', 'Wird gespeichert...') : t('stammdaten.absenden', 'Stammdaten absenden')}
            </button>

            <p className="text-xs text-center" style={{ color: '#9CA3AF' }}>
              {t('stammdaten.dsgvoHinweis', 'Deine Daten werden verschlüsselt übertragen und gemäß DSGVO verarbeitet.')}
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
